import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DialogRef } from 'app/core-lib/dialogs/modal/dialog-ref';
import { DIALOG_DATA } from 'app/core-lib/dialogs/modal/dialog-tokens';
import { CoreLibModalComponent } from 'app/core-lib/dialogs/modal/modal.component';
import {
  ChangePasswordResponse,
  UsersManagmentService,
} from 'app/core-lib/services/users-managment/users-managment.service';
import { ErrorCodes } from 'app/shared/errors.enums';
import { strengthValidator } from 'app/shared/validators/strength-password.validator';

export enum RESTORE_ACCESS_STEPS {
  menu = 0,
  restoreAccess = 1,
  changePassword = 2,
  success = 3,
}

const PASSWORD_REUSE_KEY = 'user_management.actions.change_password.password_reuse';
const GENERIC_ERROR_KEY = 'errors.login.something_went_wrong';

const isPasswordReuse = (response: ChangePasswordResponse): boolean =>
  response?.code === ErrorCodes.PASSWORD_REUSE || !!response?.details?.includes(String(ErrorCodes.PASSWORD_REUSE));

@Component({
    selector: 'mh-restore-access',
    templateUrl: './restore-access.component.html',
    styleUrls: ['./restore-access.component.scss'],
    standalone: false
})
export class RestoreAccessComponent extends CoreLibModalComponent implements OnInit, OnDestroy {
  #_state: RESTORE_ACCESS_STEPS = RESTORE_ACCESS_STEPS.menu;
  restoreAccessSteps = RESTORE_ACCESS_STEPS;
  showPassword = false;
  showRepeatPassword = false;
  newPassword;
  title;
  message;
  passwordMask;
  serverErrorKey = null;
  saving = false;
  possibleErrors = ['hasUpperCase', 'hasLowerCase', 'hasNumber', 'hasSpecialCharacters', 'hasValidLength', 'exception'];

  toggle = [false];
  toggleColors = ['tw-bg-main'];

  changePassForm = this.fb.group({
    password: ['', [Validators.required, strengthValidator()]],
    repeatPassword: ['', [Validators.required]],
  });

  private subscriptions = new Subscription();

  constructor(
    protected dialogRef: DialogRef,
    @Inject(DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private usersManagmentService: UsersManagmentService,
    private translateService: TranslateService
  ) {
    super(dialogRef, data);
  }

  ngOnInit(): void {
    this.title = this.translateService.instant('user_management.actions.restore_access.change_password');
    if (this.data?.state) {
      this.selectAction(this.data?.state);
    }
    this.subscriptions.add(this.changePassForm.valueChanges.subscribe(() => (this.serverErrorKey = null)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectAction(state: RESTORE_ACCESS_STEPS) {
    this.#_state = state;
  }

  get state() {
    return this.#_state;
  }

  changePasswordFn() {
    if (
      this.saving ||
      !this.changePassForm.valid ||
      this.passwordControl?.value !== this.repeatPasswordControl?.value
    ) {
      return;
    }

    this.serverErrorKey = null;
    this.saving = true;
    this.subscriptions.add(
      this.usersManagmentService.changePassword(this.data.userIds, this.passwordControl.value).subscribe({
        next: (response) => {
          if (isPasswordReuse(response)) return this.handleChangeError(response);
          this.handleChangeSuccess();
        },
        error: (error) => this.handleChangeError(error),
      }),
    );
  }

  private handleChangeSuccess() {
    this.saving = false;
    this.title = this.translateService.instant('user_management.actions.restore_access.new_password');
    this.message = this.translateService.instant('user_management.actions.restore_access.new_password_message', {username: `${this.data?.user?.first_name} ${this.data?.user?.last_name}`, newPassword: this.passwordControl?.value});
    this.newPassword = this.passwordControl?.value;
    this.passwordMask = '*'.repeat(this.passwordControl?.value?.length);
    this.#_state = RESTORE_ACCESS_STEPS.success;
    if (this.toggle[0] && this.data?.user?.id) {
      this.usersManagmentService.invalidatePassword(this.data?.user?.id).subscribe();
    }
  }

  private handleChangeError(response: ChangePasswordResponse) {
    this.serverErrorKey = isPasswordReuse(response) ? PASSWORD_REUSE_KEY : GENERIC_ERROR_KEY;
    this.saving = false;
  }

  restoreAccessFn() {
    this.usersManagmentService[this.data?.action](this.data?.userIds || '').subscribe(() => {
      this.title = this.translateService.instant('user_management.actions.restore_access.accesses_sent');
      this.message = this.translateService.instant('user_management.actions.restore_access.accesses_sent_message', {email: this.data?.user?.email});
      this.#_state = RESTORE_ACCESS_STEPS.success;
    });
  }

  get passwordControl() {
    return this.changePassForm.get('password');
  }

  get repeatPasswordControl() {
    return this.changePassForm.get('repeatPassword');
  }
}
